import React, { useState, useEffect, useMemo } from 'react';
import { collectionGroup, query, where, orderBy, onSnapshot, DocumentData, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import type { User } from 'firebase/auth';
import Header, { type NavLinkItem } from './Header';
import { SavedLink, getPlatformSpecificPlaceholderText } from './LinkSaver'; // Import from LinkSaver
import { formatDate } from '../utils'; // Import from utils
import styles from './PublicLinksPage.module.css';

// SVGs for placeholders (can be moved to a shared constants file)
const GENERIC_LINK_SVG_PUBLIC = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="48px" height="48px" fill="currentColor"><path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/></svg>`;


interface PublicLinksPageProps {
  userId: string | null;
  currentUser: User | null;
  onSignIn: () => void;
  onSignOut: () => void;
  navLinks: NavLinkItem[];
}

const PublicLinksPage: React.FC<PublicLinksPageProps> = ({ currentUser, onSignIn, onSignOut, navLinks }) => {
  const [publicLinks, setPublicLinks] = useState<SavedLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    setLoading(true);
    const linksQuery = query(
        collectionGroup(db, "savedLinks"), // Query across all users' savedLinks
        where("isPublic", "==", true),
        where("publicStatus", "==", "approved"),
        orderBy("submittedForPublicAt", "desc") // Order by when they were submitted (and approved)
    );

    const unsubscribe = onSnapshot(linksQuery, (querySnapshot) => {
      const fetchedLinks: SavedLink[] = querySnapshot.docs.map(docSnapshot => {
        const data = docSnapshot.data() as DocumentData;
        return {
          id: docSnapshot.id, // this ID is the link's ID, not the user's ID
          url: data.url || "",
          title: data.title || "Link không có tiêu đề",
          thumbnailUrl: data.thumbnailUrl || "",
          customThumbnailUrl: data.customThumbnailUrl,
          customThumbnailPath: data.customThumbnailPath,
          customNotes: data.customNotes,
          description: data.description,
          sourceName: data.sourceName,
          createdAt: data.createdAt as Timestamp | undefined,
          isFavorite: data.isFavorite || false, // Favorites are user-specific, may not be relevant here unless it's "public favorites"
          tags: data.tags || [],
          faviconUrl: data.faviconUrl,
          isPublic: data.isPublic || false,
          publicStatus: data.publicStatus || null,
          submittedForPublicAt: data.submittedForPublicAt as Timestamp | undefined,
          processedByAdminAt: data.processedByAdminAt as Timestamp | undefined,
          adminNotes: data.adminNotes,
        };
      });
      setPublicLinks(fetchedLinks);
      setLoading(false);
      setError(null);
    }, (err) => {
      console.error("Error fetching public links:", err);
      setError("Không thể tải danh sách links công khai.");
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handlePublicSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const filteredPublicLinks = useMemo(() =>
    publicLinks.filter(link =>
      (link.title?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
      (link.description?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
      (link.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())))
    )
  , [publicLinks, searchQuery]);

  const togglePublicFilterPanel = () => {
    console.log("Toggle filter panel for public page (placeholder)");
  };

  return (
    <div className={styles.publicLinksPageContainer}>
      <Header
        appName="Links Công Khai"
        navLinks={navLinks}
        currentUser={currentUser}
        onSignIn={onSignIn}
        onSignOut={onSignOut}
        allLinksForSuggestions={publicLinks.map(link => ({
          id: link.id,
          title: link.title,
          url: link.url,
          thumbnailUrl: link.customThumbnailUrl || link.thumbnailUrl || ""
        }))}
        onSearchQueryChange={handlePublicSearchChange}
        onToggleFilterPanel={togglePublicFilterPanel} // Placeholder, implement if needed
      />
      <main className={styles.mainContent}>
        <h2 className={styles.pageTitle}>Khám phá Links Công Khai ({filteredPublicLinks.length})</h2>
        
        {loading && <p className={styles.loadingMessage}>Đang tải links công khai...</p>}
        {error && <p className={styles.errorMessage}>{error}</p>}
        
        {!loading && !error && filteredPublicLinks.length === 0 && (
          <p className={styles.emptyMessage}>
            {publicLinks.length === 0 ? "Hiện không có link công khai nào được chia sẻ." : `Không tìm thấy link công khai nào khớp với "${searchQuery}".`}
          </p>
        )}

        {!loading && !error && filteredPublicLinks.length > 0 && (
          <div className={styles.linksGrid}>
            {filteredPublicLinks.map(link => (
              <div key={link.id} className={styles.linkCardPublic}>
                <a href={link.url} target="_blank" rel="noopener noreferrer" className={styles.cardLinkPublic}>
                  {link.customThumbnailUrl || link.thumbnailUrl ? (
                    <img 
                      src={link.customThumbnailUrl || link.thumbnailUrl} 
                      alt={link.title} 
                      className={styles.cardThumbnailImagePublic} 
                      onError={(e) => { 
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none'; 
                        const parent = target.parentElement;
                        if (parent && !parent.querySelector('.thumbnail-placeholder-public')) {
                            const placeholder = document.createElement('div');
                            placeholder.className = 'thumbnail-placeholder-public';
                            placeholder.innerHTML = getPlatformSpecificPlaceholderText(link.sourceName) || `<div dangerouslySetInnerHTML={{__html: GENERIC_LINK_SVG_PUBLIC}} />`;
                            parent.appendChild(placeholder);
                        }
                      }}
                    />
                  ) : (
                    <div className={`${styles.cardThumbnailPlaceholderPublic} thumbnail-placeholder-public`}>
                        <span>{getPlatformSpecificPlaceholderText(link.sourceName) || "Link"}</span>
                    </div>
                  )}
                  <div className={styles.cardContentPublic}>
                    <h3 className={styles.cardTitlePublic}>{link.title}</h3>
                    {link.description && <p className={styles.cardDescriptionPublic}>{link.description}</p>}
                    <div className={styles.cardMetaPublic}>
                        {link.sourceName && <span className={styles.cardSourcePublic}>{getPlatformSpecificPlaceholderText(link.sourceName)}</span>}
                        <small className={styles.cardDatePublic} title={`Ngày duyệt: ${formatDate(link.submittedForPublicAt || link.createdAt)}`}>
                        Đăng: {formatDate(link.submittedForPublicAt || link.createdAt)}
                        </small>
                    </div>
                    {link.tags && link.tags.length > 0 && (
                      <div className={styles.tagsContainerPublic}>
                        {link.tags.slice(0, 5).map(tag => <span key={tag} className={styles.tagPublic}>{tag}</span>)}
                        {link.tags.length > 5 && <span className={styles.tagMorePublic}>...</span>}
                      </div>
                    )}
                  </div>
                </a>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default PublicLinksPage;